import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiArrowRight, FiUser, FiZap, FiTarget } = FiIcons;

const FictionForm = ({ onSubmit, onBack }) => {
  const [formData, setFormData] = useState({
    title: '',
    protagonist: '',
    genre: '',
    setting: '',
    incitingIncident: '',
    conflict: '',
    stakes: '',
    themes: '',
    premise: '',
    comparableAuthors: ''
  });

  const [currentSection, setCurrentSection] = useState(0);

  const sections = [
    {
      title: 'Basic Information',
      icon: FiUser,
      fields: ['title', 'protagonist', 'genre', 'setting']
    },
    {
      title: 'Plot Elements',
      icon: FiZap,
      fields: ['incitingIncident', 'conflict', 'stakes']
    },
    {
      title: 'Themes & Style',
      icon: FiTarget,
      fields: ['themes', 'premise', 'comparableAuthors']
    }
  ];

  const formFields = {
    title: {
      label: 'Book Title',
      placeholder: 'Enter your book title',
      type: 'text',
      required: true
    },
    protagonist: {
      label: 'Main Character',
      placeholder: 'e.g., Sarah, a young widow; Pastor John; Emma, a struggling artist',
      type: 'text',
      required: true,
      hint: 'Briefly describe your protagonist with their key trait or situation'
    },
    genre: {
      label: 'Fiction Genre',
      type: 'select',
      required: true,
      options: [
        { value: '', label: 'Select genre...' },
        { value: 'romance', label: 'Christian Romance' },
        { value: 'contemporary', label: 'Contemporary Fiction' },
        { value: 'historical', label: 'Historical Fiction' },
        { value: 'thriller', label: 'Thriller/Suspense' },
        { value: 'family', label: 'Family Drama' },
        { value: 'young-adult', label: 'Young Adult' },
        { value: 'women', label: "Women's Fiction" },
        { value: 'literary', label: 'Literary Fiction' }
      ]
    },
    setting: {
      label: 'Setting',
      placeholder: 'e.g., small town Alabama, 1950s London, modern-day Seattle',
      type: 'text',
      required: true,
      hint: 'Where and when does your story take place?'
    },
    incitingIncident: {
      label: 'Inciting Incident',
      placeholder: 'e.g., discovers a family secret, loses their job, receives mysterious letter',
      type: 'textarea',
      required: true,
      hint: 'What event kicks off your story? What changes everything for your protagonist?'
    },
    conflict: {
      label: 'Main Conflict',
      placeholder: 'e.g., choosing between career and family, overcoming past trauma, fighting for justice',
      type: 'textarea',
      required: true,
      hint: 'What is the central struggle or choice your protagonist faces?'
    },
    stakes: {
      label: 'What\'s at Stake?',
      placeholder: 'e.g., losing custody of children, town being destroyed, missing chance at true love',
      type: 'textarea',
      hint: 'What will happen if your protagonist fails? What do they stand to lose or gain?'
    },
    themes: {
      label: 'Themes',
      placeholder: 'e.g., forgiveness, redemption, faith in trials, God\'s faithfulness, healing',
      type: 'text',
      hint: 'What spiritual or life themes does your book explore?'
    },
    premise: {
      label: 'Story Premise',
      placeholder: 'Write 2-3 sentences describing what your book is about...',
      type: 'textarea',
      required: true,
      hint: 'Summarize your story in a compelling way'
    },
    comparableAuthors: {
      label: 'Comparable Authors/Books',
      placeholder: 'e.g., Francine Rivers, Karen Kingsbury, Janette Oke',
      type: 'text',
      hint: 'Authors or books your readers might also enjoy'
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const nextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const prevSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const isCurrentSectionValid = () => {
    const currentFields = sections[currentSection].fields;
    return currentFields.every(field => {
      const fieldConfig = formFields[field];
      if (fieldConfig.required) {
        return formData[field] && formData[field].trim() !== '';
      }
      return true;
    });
  };

  const renderField = (fieldName) => {
    const field = formFields[fieldName];
    const value = formData[fieldName];

    return (
      <motion.div
        key={fieldName}
        className="space-y-2"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3 }}
      >
        <label className="block text-sm font-semibold text-gray-700">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        {field.type === 'select' ? (
          <select
            value={value}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            required={field.required}
          >
            {field.options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : field.type === 'textarea' ? (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
            required={field.required}
          />
        ) : (
          <input
            type={field.type}
            value={value}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            placeholder={field.placeholder}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
            required={field.required}
          />
        )}
        
        {field.hint && (
          <p className="text-sm text-gray-500 italic">{field.hint}</p>
        )}
      </motion.div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        className="bg-white rounded-2xl shadow-xl overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <SafeIcon icon={sections[currentSection].icon} className="text-2xl" />
              <div>
                <h2 className="text-2xl font-bold">Fiction Book Description</h2>
                <p className="text-blue-100">Step {currentSection + 1} of {sections.length}: {sections[currentSection].title}</p>
              </div>
            </div>
            <button
              onClick={onBack}
              className="flex items-center space-x-2 px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} />
              <span>Back</span>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-8">
          <div className="space-y-6">
            {sections[currentSection].fields.map(fieldName => renderField(fieldName))}
          </div>

          <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
            <div className="flex space-x-2">
              {sections.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full ${
                    index === currentSection 
                      ? 'bg-purple-600' 
                      : index < currentSection 
                        ? 'bg-green-500' 
                        : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <div className="flex space-x-4">
              {currentSection > 0 && (
                <button
                  type="button"
                  onClick={prevSection}
                  className="flex items-center space-x-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <SafeIcon icon={FiArrowLeft} />
                  <span>Previous</span>
                </button>
              )}

              {currentSection < sections.length - 1 ? (
                <button
                  type="button"
                  onClick={nextSection}
                  disabled={!isCurrentSectionValid()}
                  className="flex items-center space-x-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <span>Next</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!isCurrentSectionValid()}
                  className="flex items-center space-x-2 px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <span>Generate Description</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              )}
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default FictionForm;